import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { MainService } from 'src/app/app.service';
import { SmartReviewApiService } from '../../core/api/smart-review-api.service';
import {
  CreateRepositoryRequest,
  RepositoryListParams,
  RepositoryListResponse,
  RepositoryPage,
  SmartReviewRepository,
  UpdateRepositoryRequest
} from './interfaces/repositories.interface';

/** CRUD dos repositorios git cadastrados no Smart Review. */
@Injectable()
export class RepositoriesService extends SmartReviewApiService {
  private get base(): string { return `${this.apiURL}/admin/repositories`; }

  constructor(http: HttpClient, mainService: MainService) {
    super(http, mainService);
  }

  /**
   * Uma página do cadastro. Busca, status e área são aplicados no servidor: filtrar aqui
   * exigiria baixar o cadastro inteiro, que é o que a paginação existe para evitar.
   * Parâmetro vazio não vai na URL.
   */
  listRepositories(params: RepositoryListParams = {}): Observable<RepositoryPage> {
    let httpParams = new HttpParams();
    for (const [key, value] of Object.entries(params)) {
      const text = String(value ?? '').trim();
      if (text !== '') {
        httpParams = httpParams.set(key, text);
      }
    }
    return this.http
      .get<RepositoryListResponse>(this.base, { headers: this.jsonHeaders, params: httpParams })
      .pipe(map(response => {
        const items = (response?.repositories ?? []).map(repo => this.normalize(repo));
        // Sem total (API anterior à paginação) a página é o cadastro inteiro.
        return { items, total: Number(response?.total ?? items.length) || 0 };
      }));
  }

  createRepository(body: CreateRepositoryRequest): Observable<SmartReviewRepository> {
    return this.http
      .post<SmartReviewRepository>(this.base, body, { headers: this.jsonHeaders })
      .pipe(map(repo => this.normalize(repo)));
  }

  updateRepository(id: number, body: UpdateRepositoryRequest): Observable<SmartReviewRepository> {
    return this.http
      .put<SmartReviewRepository>(`${this.base}/${id}`, body, { headers: this.jsonHeaders })
      .pipe(map(repo => this.normalize(repo)));
  }

  deleteRepository(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}`, { headers: this.jsonHeaders });
  }

  private normalize(raw: SmartReviewRepository | any): SmartReviewRepository {
    return {
      id: Number(raw?.id) || 0,
      slug: raw?.slug ?? '',
      provider: raw?.provider ?? '',
      externalId: raw?.externalId ?? '',
      areaId: raw?.areaId || undefined,
      org: raw?.org || undefined,
      project: raw?.project || undefined,
      name: raw?.name ?? '',
      defaultBranch: raw?.defaultBranch || undefined,
      enabled: raw?.enabled === true,
      createdAt: raw?.createdAt || undefined,
      updatedAt: raw?.updatedAt || undefined
    };
  }
}
